import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import { playerMessagesService } from '../../../services/profileAnalyticsService';

const SENDER_TYPES = [
  { value: 'all', label: 'All Messages', icon: 'Inbox' },
  { value: 'club', label: 'Club Messages', icon: 'Building2', color: '#60a5fa', bg: 'rgba(0,100,255,0.12)', border: 'rgba(0,100,255,0.3)', activeBg: 'rgba(0,100,255,0.35)' },
  { value: 'scout', label: 'Scout Messages', icon: 'Search', color: '#34d399', bg: 'rgba(0,180,80,0.12)', border: 'rgba(0,180,80,0.3)', activeBg: 'rgba(0,180,80,0.35)' },
  { value: 'sports_director', label: 'Sports Directors', icon: 'Award', color: '#a78bfa', bg: 'rgba(150,50,255,0.12)', border: 'rgba(150,50,255,0.3)', activeBg: 'rgba(150,50,255,0.35)' },
  { value: 'fifa_agent', label: 'FIFA Agents', icon: 'Briefcase', color: '#fbbf24', bg: 'rgba(255,180,0,0.12)', border: 'rgba(255,180,0,0.3)', activeBg: 'rgba(255,180,0,0.35)' },
  { value: 'club_scouter', label: 'Club Scouters', icon: 'Users', color: '#fb7185', bg: 'rgba(255,50,80,0.12)', border: 'rgba(255,50,80,0.3)', activeBg: 'rgba(255,50,80,0.35)' },
];

const SophisticatedMessaging = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [replySuccess, setReplySuccess] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await playerMessagesService?.getForCurrentUser({ isArchived: false });
      setMessages(response?.data || []);
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await playerMessagesService?.markAsRead(messageId);
      setMessages(prev => prev?.map(m => m?.id === messageId ? { ...m, isRead: true } : m));
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleSelectMessage = (msg) => {
    setSelectedMessage(msg);
    setReplyText('');
    setReplySuccess(false);
    if (!msg?.isRead) handleMarkAsRead(msg?.id);
  };

  const handleSendReply = async (e) => {
    e?.preventDefault();
    if (!replyText?.trim() || !selectedMessage) return;
    setSendingReply(true);
    try {
      await playerMessagesService?.sendReply(selectedMessage?.id, replyText?.trim());
      setReplySuccess(true);
      setReplyText('');
      setSelectedMessage(prev => ({ ...prev, playerReply: replyText?.trim(), playerReplyAt: new Date()?.toISOString() }));
      fetchMessages();
    } catch (err) {
      console.error('Error sending reply:', err);
      alert('Failed to send reply. Please try again.');
    } finally {
      setSendingReply(false);
    }
  };

  // Count by type
  const countByType = {};
  messages?.forEach(m => {
    countByType[m?.senderType] = (countByType?.[m?.senderType] || 0) + 1;
  });
  const unreadCount = messages?.filter(m => !m?.isRead)?.length;

  // Filter messages
  const filteredMessages = activeType === 'all' ? messages : messages?.filter(m => m?.senderType === activeType);

  const getTypeInfo = (type) => SENDER_TYPES?.find(t => t?.value === type) || SENDER_TYPES?.[0];

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const now = new Date();
    const diff = Math.floor((now - date) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff} days ago`;
    return date?.toLocaleDateString();
  };

  const renderSenderDetails = (msg) => {
    const t = msg?.senderType;
    const cardStyle = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '12px' };
    const labelStyle = { color: 'rgba(180,210,255,0.5)', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '4px' };
    const valueStyle = { color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: '600' };
    if (t === 'club') return (
      <div className="grid grid-cols-2 gap-3">
        <div style={cardStyle}><p style={labelStyle}>Club Name</p><p style={valueStyle}>{msg?.senderName || '—'}</p></div>
        <div style={cardStyle}><p style={labelStyle}>Country</p><p style={valueStyle}>{msg?.senderCountry || '—'}</p></div>
        <div className="col-span-2" style={cardStyle}><p style={labelStyle}>League</p><p style={valueStyle}>{msg?.senderLeague || '—'}</p></div>
      </div>
    );
    if (t === 'scout') return (
      <div className="grid grid-cols-2 gap-3">
        <div style={cardStyle}><p style={labelStyle}>Scout Name</p><p style={valueStyle}>{msg?.senderName || '—'}</p></div>
        <div style={cardStyle}><p style={labelStyle}>Country</p><p style={valueStyle}>{msg?.senderCountry || '—'}</p></div>
      </div>
    );
    if (t === 'sports_director') return (
      <div className="grid grid-cols-2 gap-3">
        <div style={cardStyle}><p style={labelStyle}>Name</p><p style={valueStyle}>{msg?.senderName || '—'}</p></div>
        <div style={cardStyle}><p style={labelStyle}>Clubs Represented</p><p style={valueStyle}>{msg?.senderOrganization || '—'}</p></div>
      </div>
    );
    if (t === 'fifa_agent') return (
      <div className="grid grid-cols-2 gap-3">
        <div style={cardStyle}><p style={labelStyle}>Agent Name</p><p style={valueStyle}>{msg?.senderName || '—'}</p></div>
        <div style={cardStyle}><p style={labelStyle}>Agency</p><p style={valueStyle}>{msg?.senderOrganization || '—'}</p></div>
        <div className="col-span-2" style={cardStyle}><p style={labelStyle}>Country</p><p style={valueStyle}>{msg?.senderCountry || '—'}</p></div>
      </div>
    );
    if (t === 'club_scouter') return (
      <div className="grid grid-cols-2 gap-3">
        <div style={cardStyle}><p style={labelStyle}>Scouter Name</p><p style={valueStyle}>{msg?.senderName || '—'}</p></div>
        <div style={cardStyle}><p style={labelStyle}>Club</p><p style={valueStyle}>{msg?.senderOrganization || '—'}</p></div>
        <div className="col-span-2" style={cardStyle}><p style={labelStyle}>Country</p><p style={valueStyle}>{msg?.senderCountry || '—'}</p></div>
      </div>
    );
    return null;
  };

  if (loading) {
    return (
      <div className="rounded-2xl p-12 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'rgba(255,107,0,0.6)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with branding */}
      <div className="rounded-2xl p-5 relative overflow-hidden" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 60px, rgba(255,255,255,0.012) 60px, rgba(255,255,255,0.012) 61px)' }} />
        <div className="absolute top-0 left-0 w-full h-0.5" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,107,0,0.5), transparent)' }} />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,107,0,0.15)', border: '1px solid rgba(255,107,0,0.3)' }}>
              <Icon name="MessageSquare" size={20} color="#FF6B00" />
            </div>
            <div>
              <h3 className="font-black text-white text-lg">Message Center</h3>
              <p className="text-xs" style={{ color: 'rgba(180,210,255,0.6)' }}>Professional football communications</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {unreadCount > 0 && (
              <span className="px-3 py-1.5 rounded-full text-xs font-bold text-white" style={{ background: 'rgba(255,107,0,0.8)', border: '1px solid rgba(255,107,0,0.5)' }}>
                {unreadCount} unread
              </span>
            )}
            {/* PS5 + EA mini logos */}
            <div className="hidden sm:flex items-center gap-2">
              <div className="px-2.5 py-1.5 rounded-lg" style={{ background: 'rgba(255,107,0,0.1)', border: '1px solid rgba(255,107,0,0.25)' }}>
                <svg viewBox="0 0 90 30" width="60" height="20">
                  <text x="0" y="10" fontFamily="Arial Black, sans-serif" fontSize="7" fontWeight="900" fill="#FF6B00" letterSpacing="1">EA SPORTS</text>
                  <text x="0" y="24" fontFamily="Arial Black, sans-serif" fontSize="15" fontWeight="900" fill="white">FC</text>
                  <rect x="20" y="14" width="70" height="1.5" fill="#FF6B00"/>
                </svg>
              </div>
              <div className="px-2.5 py-1.5 rounded-lg" style={{ background: 'rgba(0,52,143,0.2)', border: '1px solid rgba(0,100,255,0.3)' }}>
                <svg viewBox="0 0 55 24" width="36" height="16">
                  <text x="0" y="18" fontFamily="Arial Black, sans-serif" fontSize="17" fontWeight="900" fill="white" letterSpacing="-0.5">PS5</text>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {SENDER_TYPES?.map(type => {
          const count = type?.value === 'all' ? messages?.length : (countByType?.[type?.value] || 0);
          const isActive = activeType === type?.value;
          const typeInfo = type;
          return (
            <button
              key={type?.value}
              onClick={() => { setActiveType(type?.value); setSelectedMessage(null); }}
              className="relative p-4 rounded-xl text-left transition-all hover:scale-[1.02]"
              style={isActive ? {
                background: typeInfo?.activeBg || 'rgba(255,107,0,0.25)',
                border: `2px solid ${typeInfo?.color || '#FF6B00'}`,
                boxShadow: `0 0 20px ${typeInfo?.color || '#FF6B00'}30`
              } : {
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)'
              }}
            >
              <Icon name={type?.icon} size={20} color={isActive ? (typeInfo?.color || '#FF6B00') : 'rgba(255,255,255,0.4)'} className="mb-2" />
              <p className="text-xs font-semibold leading-tight" style={{ color: isActive ? (typeInfo?.color || '#FF6B00') : 'rgba(255,255,255,0.65)' }}>{type?.label}</p>
              <p className="text-2xl font-bold mt-1" style={{ color: isActive ? 'white' : (typeInfo?.color || 'rgba(255,255,255,0.8)') }}>{count}</p>
              {type?.value !== 'all' && !isActive && count > 0 && (
                <span className="absolute top-2 right-2 w-2 h-2 rounded-full" style={{ background: typeInfo?.color || '#FF6B00' }} />
              )}
            </button>
          );
        })}
      </div>

      {/* Messages Panel */}
      <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-bold text-white">
              {getTypeInfo(activeType)?.label}
            </h3>
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}>
              {filteredMessages?.length}
            </span>
          </div>
        </div>

        <div className="flex" style={{ minHeight: '500px' }}>
          {/* Inbox List */}
          <div className={`w-full lg:w-2/5 overflow-y-auto ${selectedMessage ? 'hidden lg:block' : 'block'}`} style={{ maxHeight: '600px', borderRight: '1px solid rgba(255,255,255,0.07)' }}>
            {filteredMessages?.length === 0 ? (
              <div className="text-center py-16">
                {/* Adidas ball empty state */}
                <div className="mx-auto mb-4 opacity-20">
                  <svg viewBox="0 0 80 80" width="60" height="60" fill="white">
                    <circle cx="40" cy="40" r="36" fill="none" stroke="white" strokeWidth="2"/>
                    <polygon points="40,6 54,22 70,22 60,36 65,52 40,42 15,52 20,36 10,22 26,22" fill="white" opacity="0.5"/>
                    <circle cx="40" cy="40" r="7" fill="white"/>
                  </svg>
                </div>
                <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>No messages</p>
                <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.25)' }}>Messages will appear here when received</p>
              </div>
            ) : (
              <div>
                {filteredMessages?.map(msg => {
                  const typeInfo = getTypeInfo(msg?.senderType);
                  const isSelected = selectedMessage?.id === msg?.id;
                  return (
                    <button
                      key={msg?.id}
                      onClick={() => handleSelectMessage(msg)}
                      className="w-full text-left p-4 transition-all"
                      style={{
                        background: isSelected ? 'rgba(255,107,0,0.1)' : (!msg?.isRead ? 'rgba(255,255,255,0.03)' : 'transparent'),
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        borderLeft: isSelected ? '3px solid #FF6B00' : '3px solid transparent'
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: typeInfo?.bg || 'rgba(255,255,255,0.08)', border: `1px solid ${typeInfo?.border || 'rgba(255,255,255,0.1)'}` }}>
                          <Icon name={typeInfo?.icon || 'Mail'} size={18} color={typeInfo?.color || 'rgba(255,255,255,0.6)'} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-0.5">
                            <p className="text-sm font-semibold truncate" style={{ color: !msg?.isRead ? 'white' : 'rgba(255,255,255,0.75)', fontWeight: !msg?.isRead ? '700' : '600' }}>
                              {msg?.senderName}
                            </p>
                            {!msg?.isRead && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#FF6B00' }} />}
                          </div>
                          <span className="text-xs px-1.5 py-0.5 rounded font-medium inline-block mb-1" style={{ background: typeInfo?.bg || 'rgba(255,255,255,0.08)', color: typeInfo?.color || 'rgba(255,255,255,0.5)' }}>
                            {typeInfo?.label?.replace(' Messages', '')}
                          </span>
                          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>{formatDate(msg?.createdAt)}</p>
                          {msg?.playerReply && (
                            <span className="flex items-center gap-1 text-xs font-semibold mt-1" style={{ color: '#FF6B00' }}>
                              <Icon name="Reply" size={10} color="#FF6B00" /> Replied
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Message Detail */}
          <div className={`flex-col flex-1 overflow-hidden ${selectedMessage ? 'flex' : 'hidden lg:flex'}`}>
            {selectedMessage ? (
              <>
                {/* Message Header */}
                <div className="p-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <button
                      onClick={() => setSelectedMessage(null)}
                      className="lg:hidden flex items-center gap-1 text-sm transition-colors mr-1"
                      style={{ color: 'rgba(255,255,255,0.5)' }}
                    >
                      <Icon name="ArrowLeft" size={18} color="rgba(255,255,255,0.5)" />
                    </button>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: getTypeInfo(selectedMessage?.senderType)?.bg || 'rgba(255,255,255,0.08)', border: `1px solid ${getTypeInfo(selectedMessage?.senderType)?.border || 'rgba(255,255,255,0.1)'}` }}>
                      <Icon name={getTypeInfo(selectedMessage?.senderType)?.icon || 'Mail'} size={24} color={getTypeInfo(selectedMessage?.senderType)?.color || 'rgba(255,255,255,0.6)'} />
                    </div>
                    <div>
                      <p className="font-bold text-white text-base">{selectedMessage?.senderName}</p>
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: getTypeInfo(selectedMessage?.senderType)?.bg || 'rgba(255,255,255,0.08)', color: getTypeInfo(selectedMessage?.senderType)?.color || 'rgba(255,255,255,0.6)' }}>
                        {getTypeInfo(selectedMessage?.senderType)?.label?.replace(' Messages', '')}
                      </span>
                    </div>
                    <span className="ml-auto text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>{formatDate(selectedMessage?.createdAt)}</span>
                  </div>
                  {/* Sender Details */}
                  {renderSenderDetails(selectedMessage)}
                </div>

                {/* Message Body */}
                <div className="p-5 flex-1 overflow-y-auto">
                  <p className="whitespace-pre-line text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>{selectedMessage?.message}</p>

                  {/* Existing Reply */}
                  {selectedMessage?.playerReply && (
                    <div className="mt-6 p-4 rounded-xl" style={{ background: 'rgba(255,107,0,0.08)', border: '1px solid rgba(255,107,0,0.25)' }}>
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name="Reply" size={14} color="#FF6B00" />
                        <p className="text-xs font-bold uppercase tracking-wide" style={{ color: '#FF6B00' }}>Your Reply</p>
                        <span className="text-xs ml-auto" style={{ color: 'rgba(255,255,255,0.35)' }}>{formatDate(selectedMessage?.playerReplyAt)}</span>
                      </div>
                      <p className="text-sm whitespace-pre-line" style={{ color: 'rgba(255,255,255,0.8)' }}>{selectedMessage?.playerReply}</p>
                    </div>
                  )}
                </div>

                {/* Reply Box */}
                {!selectedMessage?.playerReply && (
                  <div className="p-4" style={{ borderTop: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.3)' }}>
                    {replySuccess ? (
                      <div className="flex items-center gap-2 py-2" style={{ color: '#34d399' }}>
                        <Icon name="CheckCircle" size={18} color="#34d399" />
                        <p className="text-sm font-semibold">Reply sent successfully!</p>
                      </div>
                    ) : (
                      <form onSubmit={handleSendReply}>
                        <label className="block text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: 'rgba(180,210,255,0.6)' }}>Reply to this message</label>
                        <textarea
                          value={replyText}
                          onChange={e => setReplyText(e?.target?.value)}
                          rows={3}
                          className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none resize-none mb-3"
                          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', outline: 'none' }}
                          placeholder="Type your reply here..."
                        />
                        <div className="flex justify-end">
                          <button
                            type="submit"
                            disabled={!replyText?.trim() || sendingReply}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90 disabled:opacity-40"
                            style={{ background: 'linear-gradient(135deg, #FF6B00, #FF9500)', boxShadow: '0 4px 15px rgba(255,107,0,0.3)' }}
                          >
                            <Icon name="Send" size={14} color="white" />
                            {sendingReply ? 'Sending...' : 'Send Reply'}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  {/* Adidas ball decoration */}
                  <div className="mx-auto mb-4 opacity-15">
                    <svg viewBox="0 0 100 100" width="80" height="80" fill="white">
                      <circle cx="50" cy="50" r="46" fill="none" stroke="white" strokeWidth="2"/>
                      <polygon points="50,7 67,28 88,28 75,48 81,70 50,56 19,70 25,48 12,28 33,28" fill="white" opacity="0.6"/>
                      <circle cx="50" cy="50" r="9" fill="white"/>
                    </svg>
                  </div>
                  <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>Select a message to read</p>
                  <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.25)' }}>Click any message from the list</p>
                  {/* PS5 branding */}
                  <div className="mt-6 flex items-center justify-center gap-3">
                    <div className="px-3 py-1.5 rounded-lg" style={{ background: 'rgba(0,52,143,0.2)', border: '1px solid rgba(0,100,255,0.2)' }}>
                      <svg viewBox="0 0 55 24" width="36" height="16">
                        <text x="0" y="18" fontFamily="Arial Black, sans-serif" fontSize="17" fontWeight="900" fill="rgba(255,255,255,0.3)" letterSpacing="-0.5">PS5</text>
                      </svg>
                    </div>
                    <div className="px-3 py-1.5 rounded-lg" style={{ background: 'rgba(255,107,0,0.08)', border: '1px solid rgba(255,107,0,0.2)' }}>
                      <svg viewBox="0 0 90 30" width="60" height="20">
                        <text x="0" y="10" fontFamily="Arial Black, sans-serif" fontSize="7" fontWeight="900" fill="rgba(255,107,0,0.4)" letterSpacing="1">EA SPORTS</text>
                        <text x="0" y="24" fontFamily="Arial Black, sans-serif" fontSize="15" fontWeight="900" fill="white">FC</text>
                        <rect x="20" y="14" width="70" height="1.5" fill="#FF6B00"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SophisticatedMessaging;